import React, { useState, useEffect } from "react";
import { Grid, Typography, Checkbox, FormControlLabel, MenuItem } from "@material-ui/core";
import styled from "styled-components";
import { CustomTextField } from "./SellAiStyledComponents";
import { publicApi } from "../../../services/ApiService";
import { sendEmailOtpUrl, verifyEmailOtpUrl, sendPhoneOtpUrl, verifyPhoneOtpUrl, caratProUserUsageUrl, sellIsdCodesUrl } from "../../../common/config/app.endpoints";

interface WizardStep5DetailsProps {
  contactForm: any;
  handleContactFormChange: any;
  isEmailVerified: boolean;
  setIsEmailVerified: (verified: boolean) => void;
  isPhoneVerified: boolean;
  setIsPhoneVerified: (verified: boolean) => void;
  usageData: any;
  setUsageData: (data: any) => void;
}

const isValidEmail = (email: string) => {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

const VerifyButton = styled.button`
  background: #FFF0F2;
  color: #d92b3c;
  border: 1px solid #FCEBEA;
  border-radius: 4px;
  padding: 0 1.25rem;
  height: 40px;
  cursor: pointer;
  font-weight: bold;
  font-size: 0.85rem;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background: #d92b3c;
    color: #FFFFFF;
    border-color: #d92b3c;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const WizardStep5Details: React.FC<WizardStep5DetailsProps> = ({
  contactForm, handleContactFormChange, isEmailVerified, setIsEmailVerified, isPhoneVerified, setIsPhoneVerified, usageData, setUsageData
}) => {
  const [showVerifyInput, setShowVerifyInput] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [showPhoneVerifyInput, setShowPhoneVerifyInput] = useState(false);
  const [phoneVerifyCode, setPhoneVerifyCode] = useState('');
  const [isdCode, setIsdCode] = useState('+1');
  const [isdCodesList, setIsdCodesList] = useState<{value: string, label: string}[]>([
    {value: '+1', label: '+1'},
    {value: '+44', label: '+44'},
    {value: '+61', label: '+61'},
    {value: '+81', label: '+81'},
    {value: '+91', label: '+91'},
    {value: '+971', label: '+971'}
  ]);
  const [isEditing, setIsEditing] = useState(!isEmailVerified);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [verifyOtpError, setVerifyOtpError] = useState('');
  const [verifyOtpMessage, setVerifyOtpMessage] = useState('');
  const [isSendingPhoneOtp, setIsSendingPhoneOtp] = useState(false);
  const [phoneOtpError, setPhoneOtpError] = useState('');
  const [phoneOtpMessage, setPhoneOtpMessage] = useState('');
  const [isVerifyingPhoneOtp, setIsVerifyingPhoneOtp] = useState(false);
  const [verifyPhoneOtpError, setVerifyPhoneOtpError] = useState('');
  const [verifyPhoneOtpMessage, setVerifyPhoneOtpMessage] = useState('');
  const [emailResendTimer, setEmailResendTimer] = useState(0);
  const [phoneResendTimer, setPhoneResendTimer] = useState(0);

  useEffect(() => {
    let timeoutId: any;
    if (isValidEmail(contactForm.email) && isEmailVerified) {
      const fullPhone = (contactForm.phone && isPhoneVerified) ? `${isdCode}-${contactForm.phone}` : "";
      timeoutId = setTimeout(() => {
        publicApi.post(caratProUserUsageUrl, {
          email: contactForm.email,
          phoneNumber: fullPhone,
          name: contactForm.name || ""
        }).then((res) => {
          setUsageData(res.data);
        }).catch(err => {
          console.error('Failed to fetch usage:', err);
          setUsageData(null);
        });
      }, 500);
    } else {
      setUsageData(null);
    }
    return () => clearTimeout(timeoutId);
  }, [contactForm.email, contactForm.phone, contactForm.name, isEmailVerified, isdCode, isPhoneVerified]);

  useEffect(() => {
    publicApi.get(sellIsdCodesUrl).then((res) => {
      const data = res.data?.data || res.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const codes = Object.entries(data).map(([key, value]) => ({
          value: `+${key}`,
          label: value as string
        }));
        if (codes.length > 0) {
          setIsdCodesList(codes);
        }
      }
    }).catch(err => console.error("Failed to fetch ISD codes", err));
  }, []);

  const getUsageText = () => {
    if (!usageData) return "Free \u00b7 1st of 2";
    if (usageData.limitExceeded) return "Paid";
    const currentNum = usageData.maxFreeSearch - usageData.freeSearchLeft + 1;
    let suffix = 'th';
    if (currentNum % 10 === 1 && currentNum % 100 !== 11) suffix = 'st';
    else if (currentNum % 10 === 2 && currentNum % 100 !== 12) suffix = 'nd';
    else if (currentNum % 10 === 3 && currentNum % 100 !== 13) suffix = 'rd';
    return `Free \u00b7 ${currentNum}${suffix} of ${usageData.maxFreeSearch}`;
  };

  useEffect(() => {
    if (isEmailVerified && !isEditing && contactForm.name) {
      const isPhoneValid = contactForm.phone ? isPhoneVerified : true;
      if (isPhoneValid) {
        setIsEditing(false);
      }
    }
  }, [isEmailVerified, isPhoneVerified, isEditing, contactForm.name, contactForm.phone]);

  useEffect(() => {
    setOtpMessage('');
    setOtpError('');
    setShowVerifyInput(false);
    setVerifyCode('');
    setVerifyOtpError('');
    setVerifyOtpMessage('');
    setEmailResendTimer(0);
  }, [contactForm.email]);

  useEffect(() => {
    setPhoneOtpMessage('');
    setPhoneOtpError('');
    setShowPhoneVerifyInput(false);
    setPhoneVerifyCode('');
    setVerifyPhoneOtpError('');
    setVerifyPhoneOtpMessage('');
    setPhoneResendTimer(0);
  }, [contactForm.phone, isdCode]);

  useEffect(() => {
    let emailInterval: any;
    if (emailResendTimer > 0) {
      emailInterval = setInterval(() => {
        setEmailResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(emailInterval);
  }, [emailResendTimer]);

  useEffect(() => {
    let phoneInterval: any;
    if (phoneResendTimer > 0) {
      phoneInterval = setInterval(() => {
        setPhoneResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(phoneInterval);
  }, [phoneResendTimer]);

  const sendEmailOtp = async () => {
    setIsSendingOtp(true);
    setOtpError('');
    setOtpMessage('');
    setVerifyCode('');
    setVerifyOtpError('');
    setVerifyOtpMessage('');
    try {
      const response = await publicApi.post(sendEmailOtpUrl, {
        name: contactForm.name,
        email: contactForm.email,
      });
      setOtpMessage(response.data?.message || 'OTP sent successfully.');
      setShowVerifyInput(true);
      setEmailResendTimer(120);
    } catch (error) {
      console.error('Failed to send email OTP:', error);
      const errData = (error as any).response?.data;
      setOtpError(errData?.errors?.[0]?.message || errData?.message || "We couldn't send the code. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyClick = () => {
    if (!contactForm.name?.trim()) {
      setOtpError('Please enter your name first.');
      return;
    }
    if (!isValidEmail(contactForm.email)) {
      setOtpError('Please enter a valid email address.');
      return;
    }
    sendEmailOtp();
  };

  const handleConfirmCode = async () => {
    if (verifyCode.length !== 6) return;

    setIsVerifyingOtp(true);
    setVerifyOtpError('');
    setVerifyOtpMessage('');
    try {
      const response = await publicApi.post(verifyEmailOtpUrl, {
        email: contactForm.email,
        otp: verifyCode,
      });
      if (response.data?.verified) {
        setVerifyOtpMessage(response.data?.message || 'Email verified successfully.');
        setIsEmailVerified(true);
        setShowVerifyInput(false);
        if (contactForm.name && (!contactForm.phone || isPhoneVerified)) {
          setIsEditing(false);
        }
      } else {
        setVerifyOtpError(response.data?.message || "That code didn't work. Please try again.");
      }
    } catch (error) {
      console.error('Failed to verify email OTP:', error);
      const errData = (error as any).response?.data;
      setVerifyOtpError(errData?.errors?.[0]?.message || errData?.message || "That code didn't work. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const sendPhoneOtp = async () => {
    setIsSendingPhoneOtp(true);
    setPhoneOtpError('');
    setPhoneOtpMessage('');
    setPhoneVerifyCode('');
    setVerifyPhoneOtpError('');
    setVerifyPhoneOtpMessage('');
    try {
      const response = await publicApi.post(sendPhoneOtpUrl, {
        phone: contactForm.phone,
        isdCode: isdCode.replace('+', ''),
      });
      setPhoneOtpMessage(response.data?.message || 'OTP sent successfully.');
      setShowPhoneVerifyInput(true);
      setPhoneResendTimer(120);
    } catch (error) {
      console.error('Failed to send phone OTP:', error);
      const errData = (error as any).response?.data;
      setPhoneOtpError(errData?.errors?.[0]?.message || errData?.message || "We couldn't send the code. Please try again.");
    } finally {
      setIsSendingPhoneOtp(false);
    }
  };

  const handlePhoneVerifyClick = () => {
    sendPhoneOtp();
  };

  const handlePhoneConfirmCode = async () => {
    if (phoneVerifyCode.length !== 6) return;

    setIsVerifyingPhoneOtp(true);
    setVerifyPhoneOtpError('');
    setVerifyPhoneOtpMessage('');
    try {
      const response = await publicApi.post(verifyPhoneOtpUrl, {
        phone: `${isdCode}${contactForm.phone}`,
        otp: phoneVerifyCode,
      });
      if (response.data?.verified) {
        setVerifyPhoneOtpMessage(response.data?.message || 'Phone verified successfully.');
        setIsPhoneVerified(true);
        setShowPhoneVerifyInput(false);
        if (isEmailVerified && contactForm.name) {
          setIsEditing(false);
        }
      } else {
        setVerifyPhoneOtpError(response.data?.message || "That code didn't work. Please try again.");
      }
    } catch (error) {
      console.error('Failed to verify phone OTP:', error);
      const errData = (error as any).response?.data;
      setVerifyPhoneOtpError(errData?.errors?.[0]?.message || errData?.message || "That code didn't work. Please try again.");
    } finally {
      setIsVerifyingPhoneOtp(false);
    }
  };

  const handleResend = (target: string) => {
    if (target === 'email' && emailResendTimer === 0) {
      sendEmailOtp();
      return;
    }
    if (target === 'phone' && phoneResendTimer === 0) {
      sendPhoneOtp();
      return;
    }
  };

  if (!isEditing && isEmailVerified) {
    return (
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Confirm where it goes</Typography>
          <Typography style={{ color: '#666', marginBottom: '2rem' }}>Your estimate appears on the next screen. We'll email you a copy too.</Typography>
        </Grid>

        <Grid item xs={12}>
          <div style={{ background: '#FFF0F2', border: '1px solid #FCEBEA', borderRadius: '8px', padding: '1rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
            <div>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1px', color: '#999', marginBottom: '0.25rem' }}>SENDING YOUR PRICE TO</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#1A1A1A', wordBreak: 'break-word' }}>
                {contactForm.name} &middot; <span style={{ color: '#d92b3c' }}>{contactForm.email}</span>
                {contactForm.phone && (
                  <> &middot; <span style={{ color: '#d92b3c' }}>{isdCode} {contactForm.phone}</span></>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              style={{ background: 'none', border: 'none', color: '#999', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', textDecoration: 'underline', cursor: 'pointer', textTransform: 'uppercase', padding: 0 }}
            >
              USE DIFFERENT DETAILS
            </button>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
            <FormControlLabel
              control={<Checkbox style={{ color: '#d92b3c' }} checked={contactForm.emailAboutUpcomingAuction || false} onChange={(e) => handleContactFormChange({ target: { name: 'emailAboutUpcomingAuction', value: e.target.checked } })} />}
              label={
                <div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>OPTIONAL</div>
                  <div style={{ fontSize: '0.85rem', color: '#1A1A1A' }}>Email me about upcoming auctions and pricing updates. Nothing to do with your estimate — you'll get that either way.</div>
                </div>
              }
              style={{ alignItems: 'flex-start', margin: 0 }}
            />
          </div>
          <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1rem' }}>
            <FormControlLabel
              control={<Checkbox style={{ color: '#d92b3c' }} checked={contactForm.usePhotoToImproveCarrotPro || false} onChange={(e) => handleContactFormChange({ target: { name: 'usePhotoToImproveCarrotPro', value: e.target.checked } })} />}
              label={
                <div>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>OPTIONAL</div>
                  <div style={{ fontSize: '0.85rem', color: '#1A1A1A' }}>Use my photos and details to improve Carat Pro's pricing. You can withdraw this at any time, and it doesn't affect your offer.</div>
                </div>
              }
              style={{ alignItems: 'flex-start', margin: 0 }}
            />
          </div>
        </Grid>

        <Grid item xs={12}>
          <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1.25rem', marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase' }}>THIS VALUATION</div>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#d92b3c' }}>{getUsageText()}</div>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.6 }}>
              Talking to our gemologist is always free — submit the piece and you'll get a firm offer at no cost, however many valuations you've run.
            </div>
          </div>
          <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#999' }}>
            We use your details to send your estimate and, if you consign, to arrange it. See our <a href="#" style={{ color: '#666', textDecoration: 'underline' }}>privacy notice</a>.
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Where should we send your price?</Typography>
        <Typography style={{ color: '#666', marginBottom: '2rem' }}>Your estimate appears on the next screen. We'll email you a copy so you have it in writing.</Typography>
      </Grid>

      <Grid item xs={12}>
        <div style={{ marginBottom: '0.5rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666' }}>NAME <span style={{ color: '#d92b3c' }}>*</span></div>
        <CustomTextField variant="outlined" fullWidth size="small" placeholder="Your name" style={{ background: '#fff' }} name="name" value={contactForm.name} onChange={handleContactFormChange} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <div style={{ marginBottom: '0.5rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666' }}>EMAIL <span style={{ color: '#d92b3c' }}>*</span></div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <CustomTextField variant="outlined" fullWidth size="small" placeholder="Email address" style={{ background: '#fff' }} name="email" value={contactForm.email} onChange={handleContactFormChange} />
          {isEmailVerified ? (
            <div style={{ background: '#ECFDF5', color: '#10B981', border: '1px solid #10B981', borderRadius: '4px', padding: '0 1rem', display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              Verified ✓
            </div>
          ) : (
            <VerifyButton
              type="button"
              onClick={handleVerifyClick}
              disabled={isSendingOtp || showVerifyInput}>
              {isSendingOtp ? 'Sending...' : (showVerifyInput ? 'Sent ✓' : 'Verify')}
            </VerifyButton>
          )}
        </div>
        {contactForm.email && !isValidEmail(contactForm.email) && (
          <div style={{ marginTop: '0.25rem', fontSize: '0.7rem', color: '#d92b3c' }}>Please enter a valid email address.</div>
        )}
        {otpError && !isEmailVerified && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#d92b3c' }}>{otpError}</div>
        )}
        {otpMessage && !otpError && !isEmailVerified && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#10B981' }}>{otpMessage}</div>
        )}
        {showVerifyInput && !isEmailVerified && (
          <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <CustomTextField
                variant="outlined"
                size="small"
                placeholder="1 2 3 4 5 6"
                style={{ background: '#fff', width: '160px' }}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                helperText="6-digit code"
                inputProps={{ style: { letterSpacing: '4px', textAlign: 'center', fontWeight: 'bold' } }}
              />
              <VerifyButton
                type="button"
                onClick={handleConfirmCode}
                disabled={verifyCode.length !== 6 || isVerifyingOtp}>
                {isVerifyingOtp ? 'Confirming...' : 'Confirm'}
              </VerifyButton>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.4 }}>
              Code sent to <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{contactForm.email}</span>.{' '}
              {emailResendTimer > 0 ? (
                <span style={{ color: '#999' }}>Resend OTP in {Math.floor(emailResendTimer / 60)}:{String(emailResendTimer % 60).padStart(2, '0')}</span>
              ) : (
                <span style={{ color: '#d92b3c', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleResend('email')}>Resend OTP</span>
              )}
              {verifyOtpError && <><br /><span style={{ color: '#d92b3c' }}>{verifyOtpError}</span></>}
              {verifyOtpMessage && !verifyOtpError && <><br /><span style={{ color: '#10B981' }}>{verifyOtpMessage}</span></>}
            </div>
          </div>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666' }}>PHONE</span>
          <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.5px', color: '#10B981', padding: '0.15rem 0.5rem', borderRadius: '12px', border: '1px solid #10B981', textTransform: 'uppercase' }}>OPTIONAL</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <CustomTextField
            select
            variant="outlined"
            size="small"
            value={isdCode}
            onChange={(e) => setIsdCode(e.target.value as string)}
            style={{ background: '#fff', width: '100px', flexShrink: 0 }}
            SelectProps={{
              renderValue: (val) => val as string,
              MenuProps: { 
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                style: { maxHeight: 300 },
                PaperProps: { style: { maxWidth: 250 } }
              }
            }}
          >
            {isdCodesList.map((code, idx) => (
              <MenuItem key={idx} value={code.value}>{code.label}</MenuItem>
            ))}
          </CustomTextField>
          <CustomTextField variant="outlined" fullWidth size="small" placeholder="Phone number" style={{ background: '#fff' }} name="phone" value={contactForm.phone || ''} onChange={(e) => handleContactFormChange({ ...e, target: { ...e.target, name: 'phone', value: e.target.value.replace(/\D/g, '') } })} />
          {isPhoneVerified ? (
            <div style={{ background: '#ECFDF5', color: '#10B981', border: '1px solid #10B981', borderRadius: '4px', padding: '0 1rem', display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
              Verified ✓
            </div>
          ) : (
            <VerifyButton
              type="button"
              onClick={handlePhoneVerifyClick}
              disabled={!contactForm.phone || isSendingPhoneOtp || showPhoneVerifyInput}>
              {isSendingPhoneOtp ? 'Sending...' : (showPhoneVerifyInput ? 'Sent ✓' : 'Verify')}
            </VerifyButton>
          )}
        </div>
        {phoneOtpError && !isPhoneVerified && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#d92b3c' }}>{phoneOtpError}</div>
        )}
        {phoneOtpMessage && !phoneOtpError && !isPhoneVerified && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#10B981' }}>{phoneOtpMessage}</div>
        )}
        {showPhoneVerifyInput && !isPhoneVerified && (
          <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <CustomTextField
                variant="outlined"
                size="small"
                placeholder="1 2 3 4 5 6"
                style={{ background: '#fff', width: '160px' }}
                value={phoneVerifyCode}
                onChange={(e) => setPhoneVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                helperText="6-digit code"
                inputProps={{ style: { letterSpacing: '4px', textAlign: 'center', fontWeight: 'bold' } }}
              />
              <VerifyButton
                type="button"
                onClick={handlePhoneConfirmCode}
                disabled={phoneVerifyCode.length !== 6 || isVerifyingPhoneOtp}>
                {isVerifyingPhoneOtp ? 'Confirming...' : 'Confirm'}
              </VerifyButton>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.4 }}>
              Code sent to <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{isdCode} {contactForm.phone}</span>.{' '}
              {phoneResendTimer > 0 ? (
                <span style={{ color: '#999' }}>Resend OTP in {Math.floor(phoneResendTimer / 60)}:{String(phoneResendTimer % 60).padStart(2, '0')}</span>
              ) : (
                <span style={{ color: '#d92b3c', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleResend('phone')}>Resend OTP</span>
              )}
              {verifyPhoneOtpError && <><br /><span style={{ color: '#d92b3c' }}>{verifyPhoneOtpError}</span></>}
              {verifyPhoneOtpMessage && !verifyPhoneOtpError && <><br /><span style={{ color: '#10B981' }}>{verifyPhoneOtpMessage}</span></>}
            </div>
          </div>
        )}
      </Grid>

      <Grid item xs={12}>
        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', marginBottom: '1rem' }}>
          We verify your email before showing the price — it's how free valuations stay free for real people. Phone is only if you'd rather we called.
        </div>
      </Grid>

      <Grid item xs={12}>
        <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
          <FormControlLabel
            control={<Checkbox style={{ color: '#d92b3c' }} checked={contactForm.emailAboutUpcomingAuction || false} onChange={(e) => handleContactFormChange({ target: { name: 'emailAboutUpcomingAuction', value: e.target.checked } })} />}
            label={
              <div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>OPTIONAL</div>
                <div style={{ fontSize: '0.85rem', color: '#1A1A1A' }}>Email me about upcoming auctions and pricing updates. Nothing to do with your estimate — you'll get that either way.</div>
              </div>
            }
            style={{ alignItems: 'flex-start', margin: 0 }}
          />
        </div>
        <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1rem' }}>
          <FormControlLabel
            control={<Checkbox style={{ color: '#d92b3c' }} checked={contactForm.usePhotoToImproveCarrotPro || false} onChange={(e) => handleContactFormChange({ target: { name: 'usePhotoToImproveCarrotPro', value: e.target.checked } })} />}
            label={
              <div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>OPTIONAL</div>
                <div style={{ fontSize: '0.85rem', color: '#1A1A1A' }}>Use my photos and details to improve Carat Pro's pricing. You can withdraw this at any time, and it doesn't affect your offer.</div>
              </div>
            }
            style={{ alignItems: 'flex-start', margin: 0 }}
          />
        </div>
      </Grid>

      <Grid item xs={12}>
        <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1.25rem', marginTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase' }}>THIS VALUATION</div>
            <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#d92b3c' }}>{getUsageText()}</div>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.6 }}>
            Talking to our gemologist is always free — submit the piece and you'll get a firm offer at no cost, however many valuations you've run.
          </div>
        </div>
        <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#999' }}>
          We use your details to send your estimate and, if you consign, to arrange it. See our <a href="#" style={{ color: '#666', textDecoration: 'underline' }}>privacy notice</a>.
        </div>
      </Grid>
    </Grid>
  );
};

export default WizardStep5Details;
